import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import Select from "react-select";

import * as Ai from "react-icons/ai";
import * as Io from "react-icons/io";
import * as Lu from "react-icons/lu";
import * as Md from "react-icons/md";

import ReservationMessages from "../components/custom/ReservationMessages";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import Utils from "../utils/Utils";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { Modal } from "antd";
import { PulseLoader } from "react-spinners";
import "../styles/style.css";

function ViewReservation() {
  const { id } = useParams();
  const {
    fetchReservationById,
    tokenData,
    reservationLoading,
    updateEnquiry,
    enquiryActionLoading,
    fetchNewReservations,
    fetchFutureReservations,
    getMessages,
    reservationSettings,
    tableLists,
    todaysReservationList,
    reservFoodpageTiming,
    foodpageReservSettings,
    closureList,
    fetchAvailableTables,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const [reservDetails, setReservDetails] = useState({
    userID: "",
    name: "",
    email: "",
    phone: "",
    bookingDate: "",
    bookingTime: "",
    formattedID: "",
    status: "",
    chairs: 0,
    message: "",
    tables: null,
  });
  const [showModal, setShowModal] = useState(false);
  const [expired, setExpired] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isError, setIsError] = useState(false);
  const [messageList, setMessageList] = useState(null);
  const [errors, setErrors] = useState({});
  const [availTables, setAvailTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(false);

  const [cancelAllowDate, setCancelAllowDate] = useState(null);
  const [timeIntervals, setTimeIntervals] = useState(null);

  const canAllocateOnAccept =
    !expired &&
    (reservDetails?.status === "Requested" ||
      reservDetails?.status === "Modified");

  const existingTableIds = useMemo(() => {
    let tableIds = reservDetails?.tables;
    if (!tableIds) return [];

    if (typeof tableIds === "string") {
      try {
        tableIds = JSON.parse(tableIds);
      } catch {
        return [];
      }
    }

    if (!Array.isArray(tableIds)) return [];

    return [
      ...new Set(
        tableIds
          .map((tableId) => Number(tableId?.id ?? tableId))
          .filter((tableId) => Number.isFinite(tableId) && tableId > 0)
      ),
    ];
  }, [reservDetails?.tables]);

  const hasExistingTables = existingTableIds.length > 0;

  const bookingDateTime = useMemo(() => {
    if (!reservDetails?.bookingDate || !reservDetails?.bookingTime) return "";
    return `${reservDetails.bookingDate} ${reservDetails.bookingTime}`;
  }, [reservDetails?.bookingDate, reservDetails?.bookingTime]);

  useEffect(() => {
    if (tokenData) {
      fetchDetails();
    }
  }, [tokenData]);

  useEffect(() => {
    if (!reservDetails?.bookingDate) return;

    const today = new Date();

    const bookedDate = new Date(reservDetails.bookingDate);
    const bookedDateOnly = new Date(
      bookedDate.getFullYear(),
      bookedDate.getMonth(),
      bookedDate.getDate()
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

    if (bookedDateOnly < todayOnly) {
      console.log("Past date. Expired.");
      setExpired(true);
      return;
    }

    if (bookedDateOnly.getTime() === todayOnly.getTime()) {
      console.log("Same date. Checking time...");
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const currentTime = `${hours}:${minutes}:${seconds}`;

      const bookingTime = reservDetails?.bookingTime;
      if (bookingTime < currentTime) {
        setExpired(true);
        return;
      }
    }

    setExpired(false);
  }, [reservDetails]);

  useEffect(() => {
    if (!tokenData || !id) return;

    getNewMessages();
  }, [tokenData]);

  useEffect(() => {
    if (!reservDetails?.bookingDate) return;

    getReservTiming(reservDetails?.bookingDate);
  }, [reservDetails?.bookingDate]);

  useEffect(() => {
    // Scheduler-created bookings already have tables â€” skip picker fetch
    if (!canAllocateOnAccept || !bookingDateTime || hasExistingTables) {
      setAvailTables([]);
      setSelectedTables(null);
      return;
    }

    let cancelled = false;

    const loadAvailableTables = async () => {
      try {
        setTablesLoading(true);
        setAvailTables([]);
        setSelectedTables(null);

        await fetchAvailableTables(
          { bookingTime: bookingDateTime },
          {
            onSuccess: (res) => {
              if (cancelled) return;
              const available = res?.data?.available ?? [];
              if (available.length === 0) {
                toast.info("No tables available for this time slot.");
              }
              setAvailTables(
                available.map((d) => ({
                  value: d?.id,
                  label: `${d?.name || "Table"}${
                    d?.chairs ? `  (${d.chairs})` : ""
                  }`,
                  meta: {
                    id: d?.id,
                    shopID: d?.shopID,
                    tableID: d?.tableID,
                    name: d?.name,
                    chairs: d?.chairs,
                    status: d?.status,
                    tableType: d?.tableType,
                  },
                }))
              );
            },
            onFailed: (err) => {
              if (cancelled) return;
              console.error("Error fetching available tables", err);
              setAvailTables([]);
            },
          }
        );
      } finally {
        if (!cancelled) setTablesLoading(false);
      }
    };

    loadAvailableTables();

    return () => {
      cancelled = true;
    };
  }, [canAllocateOnAccept, bookingDateTime, hasExistingTables]);

  const getClosureWindowsForDate = (date) => {
    if (!Array.isArray(closureList) || closureList.length === 0) return [];

    const dateObj = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObj.getTime())) return [];

    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const targetDateStr = `${dateObj.getFullYear()}-${pad(
      dateObj.getMonth() + 1
    )}-${pad(dateObj.getDate())}`;

    return closureList
      .filter((c) => c.status === "Active")
      .map((c) => {
        const startDateStr = c.startTime.slice(0, 10);
        const endDateStr = c.endTime.slice(0, 10);
        const startTimeStr = c.startTime.slice(11, 16);
        const endTimeStr = c.endTime.slice(11, 16);

        if (targetDateStr < startDateStr || targetDateStr > endDateStr) {
          return null;
        }

        let dayStart;
        let dayEnd;

        if (startDateStr === endDateStr) {
          // single-day closure
          dayStart = startTimeStr;
          dayEnd = endTimeStr;
        } else if (targetDateStr === startDateStr) {
          // first day of multi-day closure
          dayStart = startTimeStr;
          dayEnd = "23:59";
        } else if (targetDateStr === endDateStr) {
          // last day of multi-day closure
          dayStart = "00:00";
          dayEnd = endTimeStr;
        } else {
          // middle day of multi-day closure
          dayStart = "00:00";
          dayEnd = "23:59";
        }

        return { startTime: dayStart, endTime: dayEnd };
      })
      .filter(Boolean);
  };

  const getReservTiming = (date) => {
    if (!reservFoodpageTiming) return;

    setReservDetails((prev) => {
      const isDateChanged = prev.bookingDate && prev.bookingDate !== date;

      return {
        ...prev,
        bookingDate: date,
        bookingTime: isDateChanged ? "" : prev.bookingTime,
      };
    });

    const dayValue = Utils.getDayOfWeek(date);
    const todaysTiming = reservFoodpageTiming[dayValue];

    const intervalDuration =
      typeof foodpageReservSettings?.timeinterval === "string"
        ? parseInt(foodpageReservSettings?.timeinterval, 10)
        : 30;

    if (!todaysTiming || todaysTiming.length === 0) {
      setTimeIntervals([]);
      return;
    }

    //! Generate all intervals from opening/closing time
    const allIntervals = todaysTiming.flatMap((time) => {
      return Utils.getReservIntervals(
        time.openingTime,
        time.closingTime,
        intervalDuration
      );
    });

    //! Get closure window(s) for this date
    const closureWindows = getClosureWindowsForDate(date, closureList);

    if (!closureWindows || (closureWindows && closureWindows.length == 0)) {
      setTimeIntervals(allIntervals);
      return;
    }

    //! Filter out intervals that fall inside any closure window
    const filteredIntervals = allIntervals.filter((time) => {
      return closureWindows.every((win) => {
        const start = win.startTime;
        const end = win.endTime;
        return time < start || time > end;
      });
    });

    setTimeIntervals(filteredIntervals);
  };

  useEffect(() => {
    if (!reservDetails?.bookingDate || reservDetails?.bookingDate.length === 0)
      return;
    getReservTiming(reservDetails?.bookingDate);
  }, [reservDetails?.bookingDate]);

  const getNewMessages = async () => {
    const payload = {
      reservationID: id,
    };
    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    await getMessages(payload, {
      onSuccess: (res) => {
        if (res?.data) {
          setMessageList(res?.data.chatMessages);
        }
      },
      onFailed: (err) => {
        console.log("Error on fetching messages");
      },
      headers: headers,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReservDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleChairs = (value) => {
    const numberRegex = /^[0-9]*$/;
    if (numberRegex.test(value)) {
      setReservDetails((prev) => ({ ...prev, chairs: value }));
    }
  };

  const tablesDetails = useMemo(() => {
    if (!Array.isArray(tableLists) || tableLists.length === 0) return [];

    let tableIds = reservDetails?.tables;
    if (!tableIds) return [];

    if (typeof tableIds === "string") {
      try {
        tableIds = JSON.parse(tableIds);
      } catch {
        return [];
      }
    }

    if (!Array.isArray(tableIds)) return [];

    return tableIds
      .map(
        (tableId) =>
          tableLists.find((t) => t.id === tableId || t.id == tableId) || null
      )
      .filter(Boolean);
  }, [tableLists, reservDetails?.tables]);

  const fetchDetails = async () => {
    const payload = {
      reservationID: id,
    };
    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    await fetchReservationById(payload, {
      onSuccess: (res) => {
        if (res?.data && res?.data.reservationProfile) {
          const data = res?.data?.reservationProfile;
          const [date, time] = data?.bookingTime.split(" ");
          console.log("data", data);

          setReservDetails({
            userID: data?.userID,
            name: data?.name,
            email: data?.email,
            phone: data?.phone,
            bookingDate: date,
            bookingTime: time,
            chairs: data?.chairs,
            formattedID: data?.formattedID,
            message: data?.message,
            status: data?.status,
            tables: data?.tables,
          });
        }
      },
      onFailed: (err) => {
        console.log("fetch error", err);
      },
      headers: headers,
    });
  };

  const validateSelectedTablesCapacity = () => {
    if (!selectedTables || selectedTables.length === 0) return true;

    const partySize = Number(reservDetails?.chairs) || 0;
    const totalChairs = selectedTables.reduce(
      (sum, option) => sum + (parseInt(option?.meta?.chairs, 10) || 0),
      0
    );

    if (partySize > totalChairs) {
      toast.error(
        "The selected table(s) are too small for the number of people"
      );
      return false;
    }

    return true;
  };

  const handleApproveReservation = async () => {
    if (!reservDetails) return;

    if (!validateSelectedTablesCapacity()) return;

    const { status, tables, ...rest } = reservDetails;
    const headers = { "x-shopToken": tokenData?.shopCustomToken };

    // Prefer picker selection; otherwise keep scheduler-assigned tables
    const tablesFromPicker =
      selectedTables && selectedTables.length > 0
        ? [
            ...new Set(
              selectedTables.map((table) => Number(table?.value)).filter(Boolean)
            ),
          ]
        : [];
    const tablesToSend =
      tablesFromPicker.length > 0 ? tablesFromPicker : existingTableIds;

    const payload = {
      ...rest,
      status: "Approved",
      advancePayment: "",
      advanceAmount: "",
      amountStatus: "",
      shopMessage: "",
    };

    if (tablesToSend.length > 0) {
      payload.tables = tablesToSend;
    }

    await updateEnquiry(id, payload, {
      onSuccess: async () => {
        const allocated =
          tablesToSend.length > 0 ? " and tables allocated" : "";
        toast.success(`Reservation is Approved${allocated}!`);
        await fetchNewReservations(tokenData?.shopCustomToken);
        await todaysReservationList(tokenData?.shopCustomToken);
        await fetchFutureReservations(tokenData?.shopCustomToken);
        setTimeout(() => {
          navigate("/table-enquiry-list");
        }, 1000);
      },
      onFailed: (err) => {
        fetchDetails();
        const msg =
          err?.messages ?? "Reservation is not approved! Please try again";
        toast.error(msg);
      },
      headers: headers,
    });
  };

  const handleRejectReservation = async () => {
    if (!reservDetails) return;

    const { status, ...rest } = reservDetails;
    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    const payload = {
      ...rest,
      status: "Cancelled",
      advancePayment: "",
      advanceAmount: "",
      amountStatus: "",
      shopMessage: "",
    };

    await updateEnquiry(id, payload, {
      onSuccess: async (res) => {
        toast.success("Reservation is Cancelled!");
        await fetchNewReservations(tokenData?.shopCustomToken);
        await fetchFutureReservations(tokenData?.shopCustomToken);
        setTimeout(() => {
          window.location.reload("/table-enquiry-list");
        }, 1000);
      },
      onFailed: (err) => {
        console.log("Error", err);
        const msg =
          err?.messages ?? "Reservation is not approved! Please try again";
        toast.error(msg);
        fetchDetails();
      },
      headers: headers,
    });
  };

  const validateFields = () => {
    for (const key in reservDetails) {
      if (key !== "message") {
        const value = reservDetails[key];

        if (
          (typeof value === "string" && !value.trim()) ||
          (typeof value === "number" && value <= 0)
        ) {
          setIsError(true);
          return true;
        }
      }
    }

    setIsError(false);
    return false;
  };

  const isBookingValid = () => {
    const bookingDate = reservDetails?.bookingDate;
    const bookingTime = reservDetails?.bookingTime;
    const now = new Date();

    const bookingDateNew = new Date(bookingDate);

    const isSameDay =
      now.getDate() === bookingDateNew.getDate() &&
      now.getMonth() === bookingDateNew.getMonth() &&
      now.getFullYear() === bookingDateNew.getFullYear();

    if (isSameDay) {
      const [hours, minutes] = bookingTime.split(":").map(Number);

      const bookingDateTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes
      );

      return bookingDateTime < now;
    }

    return false;
  };

  const validateReservation = (reservDetails) => {
    let errors = {};

    if (!reservDetails.name.trim()) {
      errors.name = "Name is required.";
    }

    if (!reservDetails.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reservDetails.email)) {
      errors.email = "Invalid email format.";
    }

    if (!reservDetails.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else if (!/^\d{10}$/.test(reservDetails.phone)) {
      errors.phone = "Phone number must be 10 digits.";
    }

    if (!reservDetails.bookingDate.trim()) {
      errors.bookingDate = "Booking date is required.";
    }

    if (!reservDetails.bookingTime.trim()) {
      errors.bookingTime = "Booking time is required.";
    }

    if (!reservDetails.chairs || reservDetails.chairs <= 0) {
      errors.chairs = "Number of chairs must be at least 1.";
    }

    return errors;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateFields();

    // const validationErrors = validateReservation(reservDetails);

    // if (Object.keys(validationErrors).length > 0) {
    //   setErrors(validationErrors);
    //   return;
    // }
    if (isValid == true) return;
    const isValidBookingTIme = isBookingValid();

    if (isValidBookingTIme === true) {
      toast.error(`Please select a valid time!`);
      return;
    }
    if (reservDetails?.chairs > 50) {
      toast.error("Party size is must be less than 50");
      return;
    }
    if (reservDetails?.chairs <= 0) {
      toast.error("Please choose minimum party size!");
      return;
    }

    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    const payload = {
      ...reservDetails,
      advancePayment: "",
      advanceAmount: "",
      amountStatus: "",
      shopMessage: "",
    };

    await updateEnquiry(id, payload, {
      onSuccess: async (res) => {
        toast.success("Reservation Details updates successfully!");
        await fetchNewReservations(tokenData?.shopCustomToken);
        await todaysReservationList(tokenData?.shopCustomToken);
        await fetchFutureReservations(tokenData?.shopCustomToken);
        setIsEdit(false);
      },
      onFailed: (err) => {
        console.log("Error", err);
        const msg = err?.messages ?? "Updation failed! Please try again";
        toast.error(msg);
        setIsEdit(false);
        fetchDetails();
      },
      headers: headers,
    });
  };

  const today = new Date().toISOString().split("T")[0];

  const handleRefresh = async () => {
    await getNewMessages();
  };

  const statusClass =
    reservDetails?.status === "Requested"
      ? "vr-status vr-status--requested"
      : reservDetails?.status === "Modified"
      ? "vr-status vr-status--modified"
      : reservDetails?.status === "Approved"
      ? "vr-status vr-status--approved"
      : reservDetails?.status === "Cancelled" ||
        reservDetails?.status === "Rejected"
      ? "vr-status vr-status--cancelled"
      : "vr-status";

  return (
    <Fragment>
      <MasterLayout>
        <Modal
          onCancel={() => setShowModal(false)}
          open={showModal}
          footer={null}
          centered
          className="vr-modal"
        >
          <div className="vr-modal__body">
            <h3 className="vr-modal__title">Reject this reservation?</h3>
            <p className="vr-modal__text">
              This will cancel the booking request. This action cannot be undone
              from here.
            </p>
            <div className="vr-modal__actions">
              <button
                type="button"
                className="vr-btn vr-btn--danger"
                onClick={handleRejectReservation}
                disabled={enquiryActionLoading}
              >
                Yes, reject
              </button>
              <button
                type="button"
                className="vr-btn vr-btn--ghost"
                onClick={() => setShowModal(false)}
              >
                Keep request
              </button>
            </div>
          </div>
        </Modal>

        <div className="vr-page">
          <header className="vr-page__header">
            <div className="vr-page__header-left">
              <button
                type="button"
                className="vr-btn vr-btn--ghost vr-btn--icon"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <Md.MdKeyboardArrowLeft />
                <span>Back</span>
              </button>
              <div className="vr-page__titles">
                <p className="vr-page__eyebrow">Reservation</p>
                {!reservationLoading ? (
                  <h1 className="vr-page__title">
                    #{reservDetails?.formattedID ?? "N/A"}
                  </h1>
                ) : (
                  <Skeleton width={180} height={28} />
                )}
              </div>
              {!reservationLoading && (
                <span className={statusClass}>
                  {reservDetails?.status ?? "N/A"}
                </span>
              )}
            </div>

            <div className="vr-page__header-actions">
              {reservDetails?.status == "Requested" &&
                expired === false &&
                (isEdit === false ? (
                  <button
                    type="button"
                    className="vr-btn vr-btn--ghost"
                    onClick={() => setIsEdit(true)}
                  >
                    <Lu.LuClipboardEdit />
                    <span>Edit</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="vr-btn vr-btn--ghost"
                    onClick={() => setIsEdit(false)}
                  >
                    <Ai.AiOutlineCloseCircle />
                    <span>Close edit</span>
                  </button>
                ))}
              <button
                type="button"
                className="vr-btn vr-btn--ghost"
                onClick={handleRefresh}
              >
                <Io.IoMdRefresh />
                <span>Refresh</span>
              </button>
            </div>
          </header>

          {!reservationLoading && expired === true && (
            <div className="vr-alert vr-alert--warning" role="alert">
              This reservation is expired.
            </div>
          )}

          <div className="vr-shell">
            <Tabs className="vr-tabs">
              <TabList className="vr-tabs__list">
                <Tab
                  className="vr-tabs__tab"
                  selectedClassName="vr-tabs__tab is-active"
                >
                  Details
                </Tab>
                <Tab
                  className="vr-tabs__tab"
                  selectedClassName="vr-tabs__tab is-active"
                >
                  Messages
                </Tab>
              </TabList>

              <TabPanel>
                <div className="vr-layout">
                  <section className="vr-panel">
                    {!isEdit ? (
                      <>
                        <div className="vr-panel__head">
                          <h2 className="vr-panel__title">Booking details</h2>
                          <p className="vr-panel__desc">
                            Guest and schedule information for this reservation
                          </p>
                        </div>

                        <div className="vr-facts">
                          <div className="vr-fact">
                            <span className="vr-fact__label">Date</span>
                            <strong className="vr-fact__value">
                              {!reservationLoading ? (
                                reservDetails?.bookingDate ? (
                                  Utils.formatDate(reservDetails.bookingDate)
                                ) : (
                                  "N/A"
                                )
                              ) : (
                                <Skeleton width={120} />
                              )}
                            </strong>
                          </div>
                          <div className="vr-fact">
                            <span className="vr-fact__label">Time</span>
                            <strong className="vr-fact__value">
                              {!reservationLoading ? (
                                reservDetails?.bookingTime ? (
                                  Utils.convertTiming(reservDetails.bookingTime)
                                ) : (
                                  "N/A"
                                )
                              ) : (
                                <Skeleton width={100} />
                              )}
                            </strong>
                          </div>
                          <div className="vr-fact">
                            <span className="vr-fact__label">Party size</span>
                            <strong className="vr-fact__value">
                              {!reservationLoading ? (
                                reservDetails?.chairs ?? "N/A"
                              ) : (
                                <Skeleton width={60} />
                              )}
                            </strong>
                          </div>
                          <div className="vr-fact">
                            <span className="vr-fact__label">Guest</span>
                            <strong className="vr-fact__value">
                              {!reservationLoading ? (
                                reservDetails?.name ?? "N/A"
                              ) : (
                                <Skeleton width={140} />
                              )}
                            </strong>
                          </div>
                          <div className="vr-fact">
                            <span className="vr-fact__label">Phone</span>
                            <strong className="vr-fact__value">
                              {!reservationLoading ? (
                                reservDetails?.phone ?? "N/A"
                              ) : (
                                <Skeleton width={120} />
                              )}
                            </strong>
                          </div>
                          <div className="vr-fact">
                            <span className="vr-fact__label">Email</span>
                            <strong className="vr-fact__value">
                              {!reservationLoading ? (
                                reservDetails?.email ?? "N/A"
                              ) : (
                                <Skeleton width={180} />
                              )}
                            </strong>
                          </div>
                          <div className="vr-fact vr-fact--wide">
                            <span className="vr-fact__label">Message</span>
                            <strong className="vr-fact__value">
                              {!reservationLoading ? (
                                reservDetails?.message || "No message"
                              ) : (
                                <Skeleton width={220} />
                              )}
                            </strong>
                          </div>
                        </div>
                      </>
                    ) : (
                      <form className="vr-form" onSubmit={handleEditSubmit}>
                        <div className="vr-panel__head">
                          <h2 className="vr-panel__title">Edit reservation</h2>
                          <p className="vr-panel__desc">
                            Update booking and guest details
                          </p>
                        </div>

                        <p className="vr-form__section">Booking information</p>
                        <div className="vr-form__grid">
                          <div className="form-group">
                            <label className="form-label">Booking date</label>
                            <input
                              type="date"
                              name="bookingDate"
                              className="form-control"
                              value={reservDetails?.bookingDate}
                              onChange={(e) => {
                                handleChange(e);
                                getReservTiming(e.target.value);
                              }}
                              min={today}
                            />
                            {isError &&
                              reservDetails?.bookingDate.length == 0 && (
                                <p className="err__msg">
                                  Booking Date is required!
                                </p>
                              )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Booking time</label>
                            <select
                              name="bookingTime"
                              className="form-select"
                              onChange={handleChange}
                              value={
                                reservDetails?.bookingTime
                                  ? reservDetails.bookingTime.slice(0, 5)
                                  : ""
                              }
                            >
                              <option value="">Choose booking time</option>
                              {timeIntervals &&
                                timeIntervals.length != 0 &&
                                timeIntervals.map((interval, index) => (
                                  <option value={interval} key={index}>
                                    {interval
                                      ? Utils.convertTiming(interval)
                                      : "N/A"}
                                  </option>
                                ))}
                            </select>
                            {isError &&
                              reservDetails?.bookingTime.length == 0 && (
                                <p className="err__msg">
                                  Booking Time is required!
                                </p>
                              )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Party size</label>
                            <input
                              type="text"
                              name="chairs"
                              className="form-control"
                              value={reservDetails?.chairs}
                              onChange={(e) => handleChairs(e.target.value)}
                              maxLength={50}
                            />
                            {isError && reservDetails?.chairs.length == 0 && (
                              <p className="err__msg">
                                Party size is required!
                              </p>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Status</label>
                            <select
                              name="status"
                              className="form-select"
                              onChange={handleChange}
                              value={reservDetails?.status}
                            >
                              <option value="Requested">Requested</option>
                              <option value="Approved">Approved</option>
                              <option value="Rejected">Rejected</option>
                              <option value="Cancelled">Cancelled</option>
                              <option value="Completed">Completed</option>
                            </select>
                          </div>
                        </div>

                        <p className="vr-form__section">Personal information</p>
                        <div className="vr-form__grid">
                          <div className="form-group">
                            <label className="form-label">Name</label>
                            <input
                              type="text"
                              name="name"
                              className="form-control"
                              value={reservDetails?.name}
                              onChange={handleChange}
                            />
                            {isError && reservDetails?.name.length == 0 && (
                              <p className="err__msg">Name is required!</p>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Phone number</label>
                            <input
                              type="text"
                              name="phone"
                              className="form-control"
                              value={reservDetails?.phone}
                              onChange={handleChange}
                            />
                            {isError && reservDetails?.phone.length == 0 && (
                              <p className="err__msg">
                                Phone Number is required!
                              </p>
                            )}
                          </div>
                          <div className="form-group vr-form__full">
                            <label className="form-label">Email address</label>
                            <input
                              type="email"
                              name="email"
                              className="form-control"
                              value={reservDetails?.email}
                              onChange={handleChange}
                            />
                            {isError && reservDetails?.email.length == 0 && (
                              <p className="err__msg">
                                Email Address is required!
                              </p>
                            )}
                          </div>
                          <div className="form-group vr-form__full">
                            <label className="form-label">Custom message</label>
                            <textarea
                              name="message"
                              className="form-control"
                              rows={3}
                              value={reservDetails?.message}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="vr-btn vr-btn--primary"
                          disabled={enquiryActionLoading}
                        >
                          {!enquiryActionLoading ? (
                            "Save changes"
                          ) : (
                            <PulseLoader color="#fff" size={10} />
                          )}
                        </button>
                      </form>
                    )}
                  </section>

                  <aside className="vr-side">
                    <section className="vr-panel">
                      <div className="vr-panel__head">
                        <h2 className="vr-panel__title">Tables</h2>
                        <p className="vr-panel__desc">
                          Assigned tables for this booking
                        </p>
                      </div>

                      {tablesDetails && tablesDetails.length > 0 ? (
                        <ul className="vr-tables">
                          {tablesDetails.map((table) => (
                            <li key={table.id} className="vr-tables__item">
                              <strong>{table.name}</strong>
                              <span>
                                {table.chairs}{" "}
                                {Number(table.chairs) === 1
                                  ? "chair"
                                  : "chairs"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="vr-empty">
                          {reservationLoading ? (
                            <Skeleton width={160} height={20} />
                          ) : (
                            <p>No tables assigned yet.</p>
                          )}
                        </div>
                      )}
                    </section>

                    {!expired && !isEdit && (
                      <section className="vr-panel vr-panel--actions">
                        <div className="vr-panel__head">
                          <h2 className="vr-panel__title">Actions</h2>
                          <p className="vr-panel__desc">
                            Accept or reject this reservation request
                          </p>
                        </div>

                        {reservDetails?.status == "Requested" ||
                        reservDetails?.status == "Modified" ? (
                          <>
                            {hasExistingTables ? (
                              <p className="vr-hint">
                                Table already selected. Accept will keep the
                                assigned table(s).
                              </p>
                            ) : (
                              <div className="vr-allocate">
                                <label
                                  className="form-label"
                                  htmlFor="vr-allocate-tables"
                                >
                                  Allocate tables{" "}
                                  <span className="vr-hint-inline">
                                    (optional)
                                  </span>
                                </label>
                                <Select
                                  inputId="vr-allocate-tables"
                                  options={availTables}
                                  isMulti
                                  isLoading={tablesLoading}
                                  isDisabled={
                                    tablesLoading || enquiryActionLoading
                                  }
                                  value={selectedTables}
                                  onChange={(value) => setSelectedTables(value)}
                                  placeholder={
                                    tablesLoading
                                      ? "Loading tablesâ€¦"
                                      : availTables.length === 0
                                      ? "No tables available for this slot"
                                      : "Choose table(s) to allocate"
                                  }
                                  noOptionsMessage={() =>
                                    "No tables available for this slot"
                                  }
                                  menuPortalTarget={
                                    typeof document !== "undefined"
                                      ? document.body
                                      : null
                                  }
                                  menuPosition="fixed"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: 42,
                                      borderRadius: 10,
                                      borderColor: "#e5e7eb",
                                      boxShadow: "none",
                                    }),
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                                <p className="vr-hint">
                                  Leave empty to Accept without allocating. You
                                  can assign later from Unallocated.
                                </p>
                              </div>
                            )}

                            <div className="vr-actions">
                              <button
                                type="button"
                                className="vr-btn vr-btn--primary"
                                onClick={handleApproveReservation}
                                disabled={enquiryActionLoading}
                              >
                                <Ai.AiOutlineCheckCircle />
                                Accept
                              </button>
                              <button
                                type="button"
                                className="vr-btn vr-btn--danger"
                                onClick={() => setShowModal(true)}
                                disabled={enquiryActionLoading}
                              >
                                <Ai.AiOutlineCloseCircle />
                                Reject
                              </button>
                            </div>
                          </>
                        ) : reservDetails?.status == "Approved" ? (
                          <div className="vr-alert vr-alert--success">
                            Reservation is approved.
                          </div>
                        ) : reservDetails?.status == "Cancelled" ? (
                          <div className="vr-alert vr-alert--danger">
                            This reservation was cancelled.
                          </div>
                        ) : (
                          <div className="vr-empty">
                            <p>No actions available for this status.</p>
                          </div>
                        )}
                      </section>
                    )}
                  </aside>
                </div>
              </TabPanel>

              <TabPanel>
                <section className="vr-panel">
                  <ReservationMessages
                    reservDetails={reservDetails}
                    id={id}
                    messageList={messageList}
                    getNewMessages={getNewMessages}
                  />
                </section>
              </TabPanel>
            </Tabs>
          </div>
        </div>
      </MasterLayout>
    </Fragment>
  );
}

export default ViewReservation;
